/**
 * How a part is NAMED on screen. One implementation, every surface.
 *
 * ## 🔴 The rule
 *
 * **`productseq` is always the main label. `item_code` may appear beneath it,
 * never instead of it.** (Callum, 2026-08-19: *"we are using a mixture of part
 * numbers, we should ALWAYS use productseq… we can show it as a secondary
 * label but never the main label."*)
 *
 * ## ⚠️ The two columns are named BACKWARDS in the mirror (DON'T #9)
 *
 * Everything about the names suggests the opposite of the truth, which is
 * exactly why this drifted. Measured over 276,339 uk `erp_items` rows,
 * 2026-08-19:
 *
 * | column | purely numeric | what it actually holds |
 * |---|---|---|
 * | `productseq` | 7,732 — **2.8%** | the HUMAN part code: `TFE-4-X-STK`, `4460-3`, `55A1126-22-6/9-9` |
 * | `item_code` | 268,535 — **97.2%** | the numeric sequence: `110892265` |
 *
 * The same holds in our own tables — `demand_book`, `part_forecast` and
 * `part_classification` all carry 12,798 numeric `item_code`s against 230
 * numeric `productseq`s.
 *
 * ⚠️ **A comment in `blueprints/worklist.py` claimed the reverse** — that
 * `item_code` "is what a buyer actually reads off a drawing or a PO". It is
 * not; it is the sequence number. The search that comment justified is still
 * right (searching BOTH columns costs nothing and catches either), but the
 * reasoning was inverted and would have sent the next person the wrong way.
 * Corrected 2026-08-19.
 *
 * ## Why a shared helper rather than six fixed call sites
 *
 * Six report tables rendered `item_code || productseq` — the sequence number
 * first, the human code only as a fallback. Two of those endpoints do not even
 * return `item_code`, so they silently showed the right thing for the wrong
 * reason, which is the hardest kind of bug to notice. Fixing them one at a
 * time leaves the seventh to be written the old way tomorrow.
 *
 * ⚠️ `partLabel` returns HTML and escapes its own inputs. Callers must not
 * escape the result again.
 */
import { escapeHtml } from '@isg/ui-kit';

/**
 * The part's name, as a two-line block: the human code, and the sequence
 * number under it when we have one.
 *
 * ⚠️ **The secondary line is omitted when it would repeat the first.** A few
 * parts genuinely carry the same value in both columns, and printing it twice
 * looks like a rendering fault rather than a fact.
 *
 * ⚠️ Falls back to `item_code` ONLY if `productseq` is missing entirely — a
 * row with no human code is better named by its sequence than by nothing, and
 * that case should be rare enough to notice.
 */
export function partLabel(r, { mono = true, copy = true } = {}) {
  const main = r.productseq || r.item_code || '—';
  const secondary = (r.item_code && String(r.item_code) !== String(main))
    ? r.item_code : null;
  const cls = mono ? 'wl-sku' : '';
  const has = main && main !== '—';
  return `<div class="${cls} wl-sku-line">${escapeHtml(String(main))}`
    + (copy && has ? copyButton(String(main)) : '')
    + '</div>'
    + (secondary
      ? `<div class="text-xs text-muted" title="ERP item sequence">${escapeHtml(String(secondary))}</div>`
      : '');
}

/* 🔴 One click to copy the part number (Dominika, 2026-08-21: *"copying the
 * part number from intranet so we can paste it into QX. At the moment I have
 * to click, hold and slide to highlight the whole p/n"*).
 *
 * ⚠️ **`productseq` only** — the human part code, which is what QX expects
 * and what a person means by "the part number" (DON'T #9). Copying the
 * sequence number would paste something QX cannot find.
 *
 * ⚠️ It lives in `partLabel` rather than on one table, so every surface that
 * names a part gets it. Six report tables once rendered the part number six
 * different ways; fixing one at a time is what left the seventh wrong.
 */
export function copyButton(value) {
  return `<button type="button" class="wl-copy" data-copy="${escapeHtml(value)}"
            title="Copy ${escapeHtml(value)}" aria-label="Copy part number ${escapeHtml(value)}">
      <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor"
           stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <rect x="9" y="9" width="13" height="13" rx="2"/>
        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
      </svg></button>`;
}

/* One delegated listener for the whole page, bound once.
 *
 * ⚠️ **Delegated, not per-button.** These tables re-render on every filter,
 * sort and page turn, and a listener bound per row would be re-bound each
 * time and leak — and the buttons that exist at bind time are not the ones
 * that exist after the next render.
 *
 * ⚠️ `navigator.clipboard` is unavailable on an insecure origin and can be
 * refused by permission policy, so there is a fallback and a FAILURE is shown.
 * A copy button that silently does nothing has the reader paste whatever was
 * on the clipboard before — which on this app is usually another part number.
 */
export function wireCopyButtons(root = document) {
  if (root.__copyWired) return;
  root.__copyWired = true;
  /* 🔴 **CAPTURE phase, not bubble.** The first version listened on
     `document` during bubbling, so a row's own click handler — bound on the
     `<tr>`, which is INSIDE document — had already fired by the time
     `stopPropagation()` ran, and every copy opened the row's detail modal
     (Callum, 2026-08-21: *"copy button shouldnt open the details modal"*).
     Capturing runs before any handler beneath, which is the only place a
     shared control can reliably stop an event it knows nothing about.
     ⚠️ `stopImmediatePropagation` too: another capture-phase listener on the
     same node would otherwise still see it. */
  root.addEventListener('click', async (e) => {
    const btn = e.target.closest && e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    const value = btn.dataset.copy;
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
        ok = true;
      }
    } catch (err) { ok = false; }
    if (!ok) {
      try {
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        ta.remove();
      } catch (err) { ok = false; }
    }
    btn.classList.remove('is-copied', 'is-failed');
    btn.classList.add(ok ? 'is-copied' : 'is-failed');
    btn.title = ok ? `Copied ${value}` : 'Could not copy — select it manually';
    setTimeout(() => {
      btn.classList.remove('is-copied', 'is-failed');
      btn.title = `Copy ${value}`;
    }, 1400);
  }, true);

  /* ⚠️ The row is `role="button"` and keyboard-activatable, so a bare Enter
     on the focused copy control would ALSO open the row. Same guard. */
  root.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const btn = e.target.closest && e.target.closest('[data-copy]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
    btn.click();
  }, true);
}

/** The main label alone, escaped — for a cell that has no room for two lines. */
export function partName(r) {
  return escapeHtml(String(r.productseq || r.item_code || '—'));
}


/* ── The reorder code ─────────────────────────────────────────────────
 *
 * Callum, 2026-08-19 (after the team demo): *"we need the reorder code shown
 * next to the product, basically wherever the product is."*
 *
 * ⚠️ **In WORDS, never as a bare letter.** A `P` tells a buyer nothing;
 * "Production" tells them the lead time is a build rather than a purchase,
 * which is a different action entirely. That is DO #27's whole point, and this
 * app is the one the shared module was written to stop — it used to render the
 * raw letter and document only `N`.
 *
 * ⚠️ **The meanings come from the SERVER** (`isg_common.org.reorder_types`, via
 * `reorder_meaning()`). Never hardcode the letters here and never invent a
 * label for a code the server did not name — this rule already went stale once,
 * when isg-common shortened `P` from "Production (added value)" to
 * "Production" and both a doc and a test broke for being copies of a
 * definition they did not own.
 *
 * ⚠️ **TRI-STATE.** `stocked` and `made` are true, false, or NULL for
 * "we do not know" — QX carries `B` and `X` on ~150 uk parts with no confirmed
 * meaning. An unknown must never fall through to `false`, which would assert
 * the part is not held (DO #28). Hence the neutral badge and the plain label.
 */

/** Colour by what the code MEANS, not by the letter. Unknown gets neither. */
function _reorderClass(ro) {
  if (!ro) return 'badge-gray';
  if (ro.made) return 'badge-amber';       // ask about the build
  if (ro.stocked) return 'badge-green';    // held on the shelf
  if (ro.stocked === false) return 'badge-gray';  // bought against demand
  return 'badge-gray';                     // stocked === null — unknown
}

/**
 * The reorder code as a badge plus its words.
 *
 * `ro` is the server's `reorder` block; `rawCode` is the bare letter, used
 * only when the server could not name it — in which case the letter is shown
 * alone rather than dressed up in a label we invented.
 */
export function reorderBadge(ro, rawCode) {
  if (!ro) {
    return rawCode
      ? `<span class="badge badge-gray" title="This code is not in the estate vocabulary">${escapeHtml(String(rawCode))}</span>`
      : '<span class="text-muted">—</span>';
  }
  const cls = _reorderClass(ro);
  const code = ro.code
    ? `<span class="badge ${cls}">${escapeHtml(String(ro.code))}</span> ` : '';
  return `${code}${escapeHtml(String(ro.label || ''))}`;
}

/**
 * A COMPACT version for a busy row — the badge, with the words on the hover.
 *
 * ⚠️ Used where the part cell is already carrying a code, a description and a
 * warehouse. The words are still one hover away, which is the difference
 * between an abbreviation and a code nobody can read.
 */
export function reorderChip(ro, rawCode) {
  if (!ro) {
    return rawCode
      ? `<span class="badge badge-gray" title="Reorder code ${escapeHtml(String(rawCode))} — not in the estate vocabulary, so nothing is claimed about it">${escapeHtml(String(rawCode))}</span>`
      : '';
  }
  const title = [ro.label, ro.note].filter(Boolean).join(' — ');
  return `<span class="badge ${_reorderClass(ro)}" title="${escapeHtml(title)}">${escapeHtml(String(ro.label || ro.code || ''))}</span>`;
}


/* ── The reorder filter's options ─────────────────────────────────────
 *
 * Callum: *"we need to always be able to filter by reorder code."*
 *
 * ⚠️ The LETTERS are not listed here. Every list endpoint returns
 * `filters.reorder_available` from `isg_common.org.reorder_types`, and the
 * control is built from that — so a seventh code appears in the dropdown on
 * every surface the day it is added, with no frontend change. Hardcoding them
 * is what DO #27 exists to stop.
 *
 * ⚠️ The GROUPS lead, because they are the question a buyer actually asks.
 * "Show me the ones we hold" is a decision; "R or S" is an implementation
 * detail they should not need to know.
 */
const _GROUP_LABELS = {
  held: 'Held as stock',
  made: 'Made, not bought',
  bought: 'Bought against demand',
  none: 'No reorder code',
};

/**
 * Build the `<option>` list for a reorder filter.
 *
 * `available` is the server's `filters.reorder_available`. When it is missing
 * — an older build, or a response that does not carry it — the control renders
 * with just "Any", which degrades to LESS filtering rather than to a dropdown
 * of codes we invented.
 */
export function reorderOptions(available, selected = '') {
  const list = Array.isArray(available) ? available : [];
  const groups = list.filter((k) => k in _GROUP_LABELS);
  const codes = list.filter((k) => !(k in _GROUP_LABELS));
  const opt = (v, label) =>
    `<option value="${escapeHtml(v)}"${v === selected ? ' selected' : ''}>${escapeHtml(label)}</option>`;
  return [
    opt('', 'Any reorder code'),
    ...groups.map((g) => opt(g, _GROUP_LABELS[g])),
    ...(codes.length && groups.length ? ['<option disabled>──────────</option>'] : []),
    ...codes.map((code) => opt(code, code)),
  ].join('');
}


/**
 * The same control, as a report-framework filter descriptor.
 *
 * Callum, 2026-08-19: *"it needs its own column in all areas really"* — and
 * the filter travels with the column. `from` reads the server's own
 * vocabulary, so a report gets the reorder filter by adding ONE entry to its
 * spec and nothing else.
 *
 * ⚠️ Returns `{value,label}` objects rather than the HTML `reorderOptions`
 * builds, because the framework owns the markup. Both call the same grouping,
 * so the two controls cannot disagree about what `held` means.
 */
export function reorderChoices(available) {
  const list = Array.isArray(available) ? available : [];
  const groups = list.filter((k) => k in _GROUP_LABELS);
  const codes = list.filter((k) => !(k in _GROUP_LABELS));
  return [
    ...groups.map((g) => ({ value: g, label: _GROUP_LABELS[g] })),
    ...codes.map((c) => ({ value: c, label: c })),
  ];
}

export function reorderFilter() {
  return {
    param: 'reorder',
    label: 'Reorder code',
    type: 'select',
    any: 'Any reorder code',
    from: (d) => reorderChoices(
      (d.filters && d.filters.reorder_available) || d.reorder_available),
  };
}


/* ── How a part BEHAVES, and which way it is going ────────────────────
 *
 * Words, never a code — the same discipline as the reorder type, and for the
 * same reason: this app has twice shipped a letter the reader had to be
 * taught (DO #27, DO #37).
 *
 * ⚠️ TRI-STATE on `live`. A part that has never been invoiced is not "not
 * selling" — works-order components are consumed without ever reaching an
 * invoice (DO #14), so `null` means we cannot say.
 */
const _BEHAVIOUR_CLASS = {
  REGULAR: 'badge-green', INTERMITTENT: 'badge-green', SPORADIC: 'badge-gray',
  NEW: 'badge-blue', SLOWING: 'badge-amber', FADING: 'badge-amber',
  DORMANT: 'badge-red', ONE_OFF: 'badge-gray',
};

export function behaviourBadge(b) {
  if (!b) {
    return '<span class="wl-unknown" title="No invoice line in six years. That is NOT the same as never used — works-order components are consumed without being invoiced.">never sold</span>';
  }
  const m = b.meaning || {};
  const cls = _BEHAVIOUR_CLASS[b.behaviour] || 'badge-gray';
  const detail = [
    m.note,
    b.months_with_demand != null
      ? `Sold in ${b.months_with_demand} month${b.months_with_demand === 1 ? '' : 's'} of the last six years.`
      : null,
    b.months_since_last_sale != null
      ? (b.months_since_last_sale === 0
        ? 'Sold this month.'
        : `Last sold ${b.months_since_last_sale} month${b.months_since_last_sale === 1 ? '' : 's'} ago.`)
      : null,
  ].filter(Boolean).join(' ');
  return `<span class="badge ${cls}" title="${escapeHtml(detail)}">${escapeHtml(m.label || b.behaviour)}</span>`;
}

/**
 * The 12-month trend, said in full.
 *
 * 🔴 **The label carries the definition, because the number is meaningless
 * without it** (Callum, 2026-08-19: *"it just needs to be very clear what
 * it's showing"*). "+15%" alone invites the reader to supply their own
 * meaning — units? orders? margin? It is SALES VALUE, last 12 months against
 * the 12 before, and the hover names both figures.
 *
 * ⚠️ `null` is NOT 0%. It means the prior year was too small to divide by —
 * see `TREND_MIN_PRIOR_VALUE`. Rendering it as a dash with an explanation is
 * the whole point: a part that went from £100 to £1,500 is not "up 1,371%".
 */
export function trendCell(b, fmtMoney) {
  if (!b) return '<span class="text-muted">—</span>';
  const pct = b.trend_pct;
  if (pct === null || pct === undefined) {
    return '<span class="wl-unknown" title="Last year was too small to compare against — a percentage off a near-zero base would be the divisor talking, not a trend.">no comparable year</span>';
  }
  const up = pct >= 0;
  const cls = up ? 'wl-cost-fresh' : 'wl-cost-stale';
  const last = fmtMoney ? fmtMoney(b.value_last_12m) : b.value_last_12m;
  const title = `Sales value: ${last} in the last 12 months, against the 12 months before that.`;
  return `<span class="${cls}" title="${escapeHtml(title)}">${up ? '+' : ''}${Math.round(pct)}%</span>`;
}
